import { useEffect } from 'react';

/**
 * Safari-specific performance optimizations
 * This component applies various optimizations to improve Safari rendering performance
 */
export const SafariOptimizations = () => {
  useEffect(() => {
    // Detect Safari
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    if (isSafari) {
      // Apply Safari-specific optimizations
      
      // 1. Optimize scroll performance
      document.documentElement.style.scrollBehavior = 'auto';
      
      // 2. Reduce repaints by optimizing transforms
      const style = document.createElement('style');
      style.textContent = `
        /* Safari-specific performance optimizations */
        [data-safari-grid] {
          -webkit-backface-visibility: hidden;
          -webkit-perspective: 1000px;
          -webkit-transform: translate3d(0, 0, 0);
        }
        
        /* Optimize image rendering */
        img {
          -webkit-backface-visibility: hidden;
          -webkit-transform: translate3d(0, 0, 0);
          image-rendering: -webkit-optimize-contrast;
        }
        
        /* Optimize text rendering */
        body {
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          text-rendering: optimizeSpeed;
        }
        
        /* Optimize animations */
        .transition-all, .transition-transform, .transition-opacity {
          -webkit-transform: translate3d(0, 0, 0);
          will-change: transform;
        }
        
        /* Fix Safari grid rendering issues */
        [data-safari-grid] > * {
          -webkit-backface-visibility: hidden;
          -webkit-perspective: 1000px;
          -webkit-transform: translate3d(0, 0, 0);
        }
        
        /* Optimize aspect ratio containers */
        [style*="aspect-ratio"] {
          -webkit-transform: translate3d(0, 0, 0);
        }
        
        /* Fix Safari card rendering */
        .group {
          -webkit-transform: translate3d(0, 0, 0);
        }
        
        /* Fix Safari shadow rendering */
        .shadow-sm, .shadow-md, .shadow-lg, .shadow-xl, .shadow-2xl {
          -webkit-box-shadow: var(--tw-shadow, 0 0 #0000);
          box-shadow: var(--tw-shadow, 0 0 #0000);
          -webkit-transform: translate3d(0, 0, 0);
          will-change: box-shadow;
        }
        
        /* Optimize shadow transitions */
        .hover\\:shadow-md:hover, .hover\\:shadow-xl:hover, .hover\\:shadow-2xl:hover {
          -webkit-transform: translate3d(0, 0, 0);
          will-change: box-shadow;
        }
        
        /* Fix complex shadow combinations */
        .shadow-lg.shadow-black\\/5 {
          -webkit-box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(0, 0, 0, 0.05);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(0, 0, 0, 0.05);
        }
      `;
      document.head.appendChild(style);
      
      // 3. Optimize intersection observer for better performance
      if ('IntersectionObserver' in window) {
        const observerOptions = {
          rootMargin: '50px 0px',
          threshold: 0.1
        };
        
        // Apply to all images with lazy loading
        const images = document.querySelectorAll('img[loading="lazy"]');
        if (images.length > 0) {
          const imageObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                const img = entry.target as HTMLImageElement;
                img.style.transform = 'translate3d(0, 0, 0)';
                imageObserver.unobserve(img);
              }
            });
          }, observerOptions);
          
          images.forEach(img => imageObserver.observe(img));
        }
      }
      
      // 4. Fix Safari shadow rendering issues
      const fixShadowRendering = () => {
        const shadowElements = document.querySelectorAll<HTMLElement>('[class*="shadow"]');
        shadowElements.forEach(element => {
          // Force hardware acceleration for shadow elements
          element.style.transform = 'translate3d(0,0,0)';
          element.style.willChange = 'box-shadow';
          
          // Fix complex shadow combinations
          if (element.classList.contains('shadow-lg') && element.classList.contains('shadow-black/5')) {
            element.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(0, 0, 0, 0.05)';
          }
        });
      };
      
      // 5. Fix Safari grid layout issues
      const fixGridLayout = () => {
        // Restrict tweaks to explicitly marked grids only.
        const grids = document.querySelectorAll<HTMLElement>('[data-safari-grid]');
        grids.forEach(grid => {
          // Apply Safari-specific fixes to non-interactive grid descendants
          // to avoid interfering with click/tap hit-testing on links/buttons.
          const gridItems = grid.querySelectorAll<HTMLElement>('*');
          gridItems.forEach(item => {
            const tag = item.tagName.toLowerCase();
            if (
              tag === 'a' ||
              tag === 'button' ||
              tag === 'input' ||
              tag === 'select' ||
              tag === 'textarea' ||
              tag === 'label'
            ) {
              return;
            }

            const inlineTransform = item.style.transform;
            const computedTransform = getComputedStyle(item).transform;

            // Skip elements that already have a computed transform applied
            if (computedTransform && computedTransform !== 'none') {
              return;
            }

            const hasInlineTransform = inlineTransform && inlineTransform.trim() !== '' && inlineTransform !== 'none';

            if (!hasInlineTransform) {
              item.style.transform = 'translate3d(0,0,0)';
            } else if (!inlineTransform.includes('translate3d')) {
              const existingWillChange = item.style.willChange;
              const willChangeValues = existingWillChange
                ? existingWillChange.split(',').map(value => value.trim())
                : [];

              if (!willChangeValues.includes('transform')) {
                item.style.willChange = existingWillChange
                  ? `${existingWillChange}, transform`
                  : 'transform';
              }
            }
          });
        });
      };
      
      // Run fixes after DOM is ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          fixShadowRendering();
          fixGridLayout();
        });
      } else {
        fixShadowRendering();
        fixGridLayout();
      }
      
      // 5. Optimize scroll events
      let ticking = false;
      const optimizeScroll = () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            // Scroll optimization logic here
            ticking = false;
          });
          ticking = true;
        }
      };
      
      window.addEventListener('scroll', optimizeScroll, { passive: true });
      
      // Cleanup
      return () => {
        window.removeEventListener('scroll', optimizeScroll);
        if (style.parentNode) {
          style.parentNode.removeChild(style);
        }
      };
    }
  }, []);

  return null;
};

export default SafariOptimizations;
